import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axiosConfig';
import { useAuth } from '../context/AuthContext';
import Layout from './Layout';
import { 
  TrashIcon, 
  PencilIcon, 
  MagnifyingGlassIcon, 
  QrCodeIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { parse } from 'papaparse';


import productAPI from '../api/productAPI';

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [userStores, setUserStores] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [file, setFile] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [templateDownloading, setTemplateDownloading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  const paginationSpread = 1;

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending'
  });

  const [formData, setFormData] = useState({
    store: '',
    sku: '',
    barcode: '',
    name: '',
    price: '',
    cost: '',
    uom: 'each',
    isActive: true,
    reorderPoint: 0,
    category: '',
    vendor: ''
  });

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
  const { getAccessToken, isAuthenticated } = useAuth();

  const normalizeVendorList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data?.vendors)) return payload.data.vendors;
    if (Array.isArray(payload?.vendors)) return payload.vendors;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = getAccessToken();

        console.log('productAPI token', token);

        // Fetch products
        const productRes = await productAPI.getAll();

        console.log('Products data:', productRes);
        
        // Handle both response formats: { data } or direct array
        const productsData = productRes.data?.data || productRes.data;
        setProducts(Array.isArray(productsData) ? productsData : []);


        // Fetch categories
        const categoryRes = await api.get('/product-categories');
        setCategories(categoryRes.data?.data || []);

        // Fetch vendors
        const vendorRes = await api.get('/vendors');
        setVendors(normalizeVendorList(vendorRes.data));

        // Fetch user's stores
        const storesRes = await api.get('/users/stores');
        setUserStores(storesRes.data || []);

      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data. Please try again.');
        setProducts([]);
        setCategories([]);
        setVendors([]);
        setUserStores([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [apiUrl]);

  // Request sort function
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  // Filter products safely - moved this before sortedProducts
  const filteredProducts = useMemo(() => {
    return Array.isArray(products) 
      ? products.filter(product => {
          if (!product) return false;
          const searchLower = searchTerm.toLowerCase();
          const nameMatch = product.name?.toLowerCase().includes(searchLower);
          const skuMatch = product.sku?.toLowerCase().includes(searchLower);
          const barcodeMatch = product.barcode?.toLowerCase().includes(searchLower);
          return nameMatch || skuMatch || barcodeMatch;
        })
      : [];
  }, [products, searchTerm]);

  // Sort products - now this can safely use filteredProducts
  const sortedProducts = useMemo(() => {
    let sortableProducts = [...filteredProducts];
    if (sortConfig.key) {
      sortableProducts.sort((a, b) => {
        // Handle null/undefined values
        if (a[sortConfig.key] == null) return sortConfig.direction === 'ascending' ? 1 : -1;
        if (b[sortConfig.key] == null) return sortConfig.direction === 'ascending' ? -1 : 1;
        
        // Numeric comparison for price, cost, reorderPoint
        if (['price', 'cost', 'reorderPoint'].includes(sortConfig.key)) {
          return sortConfig.direction === 'ascending' 
            ? a[sortConfig.key] - b[sortConfig.key] 
            : b[sortConfig.key] - a[sortConfig.key];
        }
        
        // String comparison for other fields
        if (a[sortConfig.key].toLowerCase() < b[sortConfig.key].toLowerCase()) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key].toLowerCase() > b[sortConfig.key].toLowerCase()) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableProducts;
  }, [filteredProducts, sortConfig]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Open "Add Product" modal
  const openAddModal = () => {
    setEditingProduct(null);
    setFormData({
      store: '',
      sku: '',
      barcode: '',
      name: '',
      price: '',
      cost: '',
      uom: 'each',
      isActive: true,
      reorderPoint: 0,
      category: '',
      vendor: ''
    });
    setModalOpen(true);
  };

  // Open "Edit Product" modal
  const openEditModal = (product) => {
    if (!product) return;
    
    setEditingProduct(product);
    setFormData({
      store: product.store || '',
      sku: product.sku || '',
      barcode: product.barcode || '',
      name: product.name || '',
      price: product.price || '',
      cost: product.cost || '',
      uom: product.uom || 'each',
      isActive: product.isActive !== false,
      reorderPoint: product.reorderPoint || 0,
      category: product.category || '',
      vendor: product.vendor || ''
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setShowScanner(false);
    setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle barcode scan
  const handleBarcodeScan = (barcode) => {
    setFormData(prev => ({ ...prev, barcode: barcode || '' }));
    setShowScanner(false);
  };

  // Save product (add or update)
  const handleSaveProduct = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      const token = getAccessToken();
      
      // Prepare data - convert empty values to undefined
      const productData = {
        ...formData,
        barcode: formData.barcode?.trim() || undefined,
        vendor: formData.vendor?.trim() || undefined,
        price: parseFloat(formData.price) || 0,
        cost: parseFloat(formData.cost) || 0,
        reorderPoint: parseInt(formData.reorderPoint) || 0
      };

      if (!productData.vendor) {
        delete productData.vendor;
      }
  
      let response;
      if (editingProduct) {
        response = await api.put(
          `/products/${editingProduct._id}`,
          productData
        );
        // Update the existing product in the list
        setProducts(prev => prev.map(p => p._id === editingProduct._id ? response.data.data : p));
      } else {
        response = await api.post(
          '/products',
          productData
        );
        // Add the new product to the beginning of the list
        setProducts(prev => [response.data.data, ...prev]);
      }
      closeModal();
    } catch (error) {
      console.error('Error saving product:', error);
      if (error.response?.data?.code === 11000) {
        setError('A product with this SKU or barcode already exists');
      } else {
        setError(error.response?.data?.message || 'Failed to save product');
      }
    }
  };

  // Delete product
  const handleDeleteProduct = async (productId) => {
    try {
      setError(null);
      await api.delete(`/products/${productId}`);
      setProducts(prev => prev.filter(p => p._id !== productId));
    } catch (error) {
      console.error('Error deleting product:', error);
      setError('Failed to delete product');
    }
  };

  // Handle file upload for import
  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    setFile(uploadedFile);
    setImportErrors([]);
    setImportStatus(null);

    parse(uploadedFile, {
      header: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          setImportErrors(results.errors);
          return;
        }
        setCsvData(results.data);
      },
      error: (error) => {
        setImportErrors([{ message: error.message }]);
      }
    });
  };

  // Download CSV template
  const downloadTemplate = async () => {
    try {
      setTemplateDownloading(true);
      const token = getAccessToken();
      
      // Get stores, categories, and vendors for the template
      const [storesRes, categoriesRes, vendorsRes] = await Promise.all([
        api.get('/users/stores'),
        api.get('/product-categories'),
        api.get('/vendors'),
      ]);

      const stores = storesRes.data || [];
      const categories = categoriesRes.data?.data || [];
      const vendors = normalizeVendorList(vendorsRes.data);

      // Create template headers with example values
      const headers = [
        'store', 
        'sku', 
        'barcode', 
        'name', 
        'price', 
        'cost', 
        'uom', 
        'isActive', 
        'reorderPoint', 
        'category', 
        'vendor'
      ];

      // Create example row with valid values from the system
      const exampleRow = {
        store: stores[0]?._id || 'store_id_1',
        sku: 'SKU-001',
        barcode: '123456789012',
        name: 'Example Product',
        price: '9.99',
        cost: '5.50',
        uom: 'each',
        isActive: 'true',
        reorderPoint: '10',
        category: categories[0]?._id || 'category_id_1',
        vendor: vendors[0]?._id || 'vendor_id_1'
      };

      // Create CSV content
      let csvContent = headers.join(',') + '\n';
      csvContent += headers.map(h => exampleRow[h]).join(',') + '\n';

      // Add notes
      csvContent += '\nNotes:\n';
      csvContent += '- Required fields: store, sku, name, price, cost, category\n';
      csvContent += '- Store must be a valid store ID from your account\n';
      csvContent += '- Category must be a valid category ID\n';
      csvContent += '- Vendor must be a valid vendor ID or can be empty\n';
      csvContent += '- isActive should be "true" or "false"\n';
      csvContent += '- uom options: each, box, case, kg, liter, meter, bottle, roll, jar\n';

      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', 'product_import_template.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTemplateDownloading(false);
    } catch (error) {
      console.error('Error downloading template:', error);
      setTemplateDownloading(false);
      setError('Failed to download template');
    }
  };

  // Process the imported data
  const processImport = async () => {
    if (!csvData.length || isImporting) return;

    setIsImporting(true);
    setImportProgress(0);
    setImportStatus('Importing products...');
    setImportErrors([]);

    const token = getAccessToken();
    const totalProducts = csvData.length;
    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    for (let i = 0; i < csvData.length; i++) {
      const row = csvData[i];
      try {
        // Prepare product data
        const productData = {
          store: row.store,
          sku: row.sku,
          barcode: row.barcode || undefined,
          name: row.name,
          price: parseFloat(row.price) || 0,
          cost: parseFloat(row.cost) || 0,
          uom: row.uom || 'each',
          isActive: row.isActive === 'true' || row.isActive === true || row.isActive === '1',
          reorderPoint: parseInt(row.reorderPoint) || 0,
          category: row.category,
          vendor: row.vendor?.trim() || undefined
        };

        if (!productData.vendor) {
          delete productData.vendor;
        }

        console.log('import product', productData);
    
        await api.post(
          '/products',
          productData
        );
    
        successCount++;
      } catch (error) {
        errorCount++;
        errors.push({
          row: i + 2, // +1 for header, +1 for 0-based index
          sku: row.sku,
          error: error.response?.data?.message || error.message || 'Unknown error'
        });
      }
    
      // Update progress
      setImportProgress(Math.floor(((i + 1) / totalProducts) * 100));
    }

    // Update status
    setImportStatus(`Import complete: ${successCount} successful, ${errorCount} failed`);
    setImportErrors(errors);
    setIsImporting(false);

    // Refresh products if any were successful
    if (successCount > 0) {
      fetchProducts();
    }
  };

  // Fetch products (extracted from useEffect for reuse)
  const fetchProducts = async () => {
    try {
      const token = getAccessToken();
      const productRes = await api.get('/products');
      const productsData = productRes.data?.data || productRes.data;
      setProducts(Array.isArray(productsData) ? productsData : []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  // Reset import modal
  const resetImport = () => {
    setFile(null);
    setCsvData([]);
    setImportProgress(0);
    setImportStatus(null);
    setImportErrors([]);
  };

  //Format number
  const formatNumber = (num) => {
    return num?.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) || '0.00';
  };

  return (
    <Layout pageTitle="Product Management">
      <div className="bg-gray-100 mx-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="relative mr-4">
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border p-2 pl-10 w-64"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            </div>
          <div className="flex items-center">
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setImportModalOpen(true)}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center"
              >
                <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                Import
              </button>
              <button
                onClick={openAddModal}
                className="bg-teal-500 text-white px-4 py-2 rounded hover:bg-teal-600"
              >
                + Add Product
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
          </div>
        ) : (
          /* Products Table */
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {filteredProducts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {products.length === 0 ? 'No products found' : 'No products match your search'}
              </div>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                        onClick={() => requestSort('name')}
                      >
                        <div className="flex items-center">
                          Name
                          {sortConfig.key === 'name' && (
                            sortConfig.direction === 'ascending' ? 
                              <ChevronUpIcon className="ml-1 h-4 w-4" /> : 
                              <ChevronDownIcon className="ml-1 h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                        onClick={() => requestSort('barcode')}
                      >
                        <div className="flex items-center">
                          Barcode
                          {sortConfig.key === 'barcode' && (
                            sortConfig.direction === 'ascending' ? 
                              <ChevronUpIcon className="ml-1 h-4 w-4" /> : 
                              <ChevronDownIcon className="ml-1 h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                        onClick={() => requestSort('price')}
                      >
                        <div className="flex items-center">
                          Price
                          {sortConfig.key === 'price' && (
                            sortConfig.direction === 'ascending' ? 
                              <ChevronUpIcon className="ml-1 h-4 w-4" /> : 
                              <ChevronDownIcon className="ml-1 h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                        onClick={() => requestSort('cost')}
                      >
                        <div className="flex items-center">
                          Cost
                          {sortConfig.key === 'cost' && (
                            sortConfig.direction === 'ascending' ? 
                              <ChevronUpIcon className="ml-1 h-4 w-4" /> : 
                              <ChevronDownIcon className="ml-1 h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                        onClick={() => requestSort('uom')}
                      >
                        <div className="flex items-center">
                          UOM
                          {sortConfig.key === 'uom' && (
                            sortConfig.direction === 'ascending' ? 
                              <ChevronUpIcon className="ml-1 h-4 w-4" /> : 
                              <ChevronDownIcon className="ml-1 h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                        onClick={() => requestSort('reorderPoint')}
                      >
                        <div className="flex items-center">
                          Re-order Point
                          {sortConfig.key === 'reorderPoint' && (
                            sortConfig.direction === 'ascending' ? 
                              <ChevronUpIcon className="ml-1 h-4 w-4" /> : 
                              <ChevronDownIcon className="ml-1 h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      >
                        <div className="flex items-center">
                          Status
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentItems.map((product) => {
                      const categoryName = categories.find(c => c._id === product?.category)?.name;
                      const storeName = userStores.find(s => s._id === product?.store)?.name;

                      return (
                        <tr key={product?._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{product?.name || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product?.barcode || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatNumber(product?.price)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatNumber(product?.cost)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product?.uom || 'each'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product?.reorderPoint}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              product?.isActive !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {product?.isActive !== false ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => openEditModal(product)}
                                className="text-teal-600 hover:text-teal-900"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => product?._id && handleDeleteProduct(product._id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                        <span className="font-medium">
                          {Math.min(indexOfLastItem, sortedProducts.length)}
                        </span>{' '}
                        of <span className="font-medium">{sortedProducts.length}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => paginate(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Previous</span>
                          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                        
                        {/* Always show first page */}
                        <button
                          onClick={() => paginate(1)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === 1
                              ? 'z-10 bg-teal-50 border-teal-500 text-teal-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          1
                        </button>

                        {/* Show ellipsis if needed */}
                        {currentPage > paginationSpread + 2 && (
                          <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                            ...
                          </span>
                        )}

                        {/* Middle pages */}
                        {Array.from({ length: Math.min(paginationSpread * 2 + 1, totalPages - 2) }, (_, i) => {
                          const page = Math.max(2, Math.min(
                            currentPage - paginationSpread + i,
                            totalPages - 1
                          ));
                          if (page < 2 || page >= totalPages) return null;
                          return (
                            <button
                              key={page}
                              onClick={() => paginate(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === page
                                  ? 'z-10 bg-teal-50 border-teal-500 text-teal-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}

                        {/* Show ellipsis if needed */}
                        {currentPage < totalPages - paginationSpread - 1 && (
                          <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                            ...
                          </span>
                        )}

                        {/* Always show last page */}
                        {totalPages > 1 && (
                          <button
                            onClick={() => paginate(totalPages)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === totalPages
                                ? 'z-10 bg-teal-50 border-teal-500 text-teal-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {totalPages}
                          </button>
                        )}
                        
                        <button
                          onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Next</span>
                          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
              </>
            )}
          </div>
        )}

        {/* Product Modal */}
        {modalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
              <div className="p-6">
                <h2 className="text-xl font-bold mb-4">
                  {editingProduct ? 'Edit Product' : 'Add Product'}
                </h2>
                
                {error && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSaveProduct} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Column 1 */}
                  <div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Store*</label>
                      <select
                        name="store"
                        value={formData.store}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                        required
                      >
                        <option value="">Select Store</option>
                        {userStores.map(store => (
                          <option key={store._id} value={store._id}>{store.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">SKU*</label>
                      <input
                        type="text"
                        name="sku"
                        value={formData.sku}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                        required
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Barcode</label>
                      <div className="flex">
                        <input
                          type="text"
                          name="barcode"
                          value={formData.barcode || ''}
                          onChange={handleInputChange}
                          className="mt-1 block w-full border border-gray-300 rounded-l-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowScanner(true)}
                          className="inline-flex items-center px-3 rounded-r-md bg-gray-100 border border-l-0 border-gray-300 text-gray-500 hover:bg-gray-200"
                        >
                          <QrCodeIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name*</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                        required
                      />
                    </div>
                  </div>

                  {/* Column 2 */}
                  <div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Price*</label>
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        step="0.01"
                        min="0"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                        required
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cost*</label>
                      <input
                        type="number"
                        name="cost"
                        value={formData.cost}
                        onChange={handleInputChange}
                        step="0.01"
                        min="0"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                        required
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Unit of Measure*</label>
                      <select
                        name="uom"
                        value={formData.uom}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                      >
                        <option value="each">Piece</option>
                        <option value="box">Box</option>
                        <option value="case">Case</option>
                        <option value="kg">Kilogram</option>
                        <option value="liter">Liter</option>
                        <option value="meter">Meter</option>
                        <option value="bottle">Bottle</option>
                        <option value="roll">Roll</option>
                        <option value="jar">Jar</option>
                      </select>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Re-order Point</label>
                      <input
                        type="number"
                        name="reorderPoint"
                        value={formData.reorderPoint}
                        onChange={handleInputChange}
                        min="0"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>

                    <div className="mb-4 flex items-center">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-700">Active Product</label>
                    </div>
                  </div>

                  {/* Full width fields */}
                  <div className="md:col-span-2">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category*</label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.map(cat => (
                          <option key={cat._id} value={cat._id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
                      <select
                        name="vendor"
                        value={formData.vendor}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                      >
                        <option value="">Select Vendor</option>
                        {(Array.isArray(vendors) ? vendors : []).map(v => (
                          <option key={v._id} value={v._id}>{v.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="md:col-span-2 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                    >
                      {editingProduct ? 'Update Product' : 'Add Product'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Barcode Scanner Modal */}
        {showScanner && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="p-6">
                <h2 className="text-xl font-bold mb-4">Scan Barcode</h2>
                <div className="bg-gray-200 p-4 rounded-lg text-center mb-4">
                  <p className="text-gray-600">Barcode scanner would appear here</p>
                  <input
                    type="text"
                    value={formData.barcode}
                    onChange={(e) => handleBarcodeScan(e.target.value)}
                    className="mt-2 border p-2 w-full"
                    placeholder="Enter barcode manually"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowScanner(false)}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Import Products Modal */}
        {importModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Import Products</h2>
                  <button onClick={() => { setImportModalOpen(false); resetImport(); }}>
                    <XMarkIcon className="h-6 w-6 text-gray-500 hover:text-gray-700" />
                  </button>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                    {error}
                  </div>
                )}

                <div className="mb-6">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    {!file ? (
                      <div>
                        <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-600">
                          Drag and drop your CSV file here, or click to browse
                        </p>
                        <input
                          type="file"
                          id="csv-upload"
                          accept=".csv"
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                        <label
                          htmlFor="csv-upload"
                          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
                        >
                          Select File
                        </label>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm font-medium text-gray-700">
                          Selected file: {file.name}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {csvData.length} products found
                        </p>
                        <button
                          onClick={resetImport}
                          className="mt-4 text-sm text-blue-600 hover:text-blue-800"
                        >
                          Change file
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex justify-center">
                    <button
                      onClick={downloadTemplate}
                      disabled={templateDownloading}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                      {templateDownloading ? 'Downloading...' : 'Download CSV Template'}
                    </button>
                  </div>
                </div>

                {importStatus && (
                  <div className={`mb-4 p-3 rounded ${
                    importErrors.length > 0 ? 'bg-yellow-100 border border-yellow-400 text-yellow-700' : 'bg-green-100 border border-green-400 text-green-700'
                  }`}>
                    {importStatus}
                  </div>
                )}

                {importProgress > 0 && (
                  <div className="mb-4">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-blue-600 h-2.5 rounded-full"
                        style={{ width: `${importProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 text-center">
                      {importProgress}% complete
                    </p>
                  </div>
                )}

                {importErrors.length > 0 && (
                  <div className="mb-4 max-h-60 overflow-y-auto">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Import Errors:</h3>
                    <div className="bg-red-50 border border-red-200 rounded-md p-2">
                      <table className="min-w-full divide-y divide-red-200">
                        <thead>
                          <tr>
                            <th className="px-3 py-1 text-left text-xs font-medium text-red-800 uppercase tracking-wider">Row</th>
                            <th className="px-3 py-1 text-left text-xs font-medium text-red-800 uppercase tracking-wider">SKU</th>
                            <th className="px-3 py-1 text-left text-xs font-medium text-red-800 uppercase tracking-wider">Error</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-red-200">
                          {importErrors.map((err, idx) => (
                            <tr key={idx}>
                              <td className="px-3 py-1 text-sm text-red-700">{err.row}</td>
                              <td className="px-3 py-1 text-sm text-red-700">{err.sku || '-'}</td>
                              <td className="px-3 py-1 text-sm text-red-700">{err.error}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {csvData.length > 0 && (
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => { setImportModalOpen(false); resetImport(); }}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={processImport}
                      disabled={isImporting}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isImporting ? 'Importing...' : 'Start Import'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProductsPage;